import {
  BiddingStrategySchema,
  type CompiledTemplateCampaign,
  type Placement,
  type TemplateCampaignOverride,
} from "@adpilot/shared";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  IconChevronLeft,
  IconChevronRight,
  IconPencil,
  IconTrash,
  IconX,
} from "@tabler/icons-react";
import { useMemo, useRef, useState } from "react";
import { Controller, type FieldPath, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Sheet, SheetContent, SheetDescription, SheetTitle } from "@/components/ui/sheet";
import { Tabs, TabsList, TabsPanel, TabsTab } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";
import {
  BIDDING_STRATEGIES,
  campaignDefaultBid,
  campaignTargets,
  groupOfLayer,
  isAutomatic,
  mergeOverride,
  PLACEMENTS,
  type PreviewTarget,
} from "./campaign-model";
import { AffixInput, parseAmount } from "./fields";

const PAGE_SIZE = 10;
const MAX_NEGATIVE_LENGTH = 80;

const positiveAmount = z.string().refine((text) => (parseAmount(text) ?? 0) > 0);
const placementPercent = z.string().refine((text) => {
  const value = parseAmount(text);
  return (
    text.trim() === "" || (value !== null && Number.isInteger(value) && value >= 0 && value <= 900)
  );
});

const settingsSchema = z.object({
  name: z.string().trim().min(1).max(128),
  dailyBudget: positiveAmount,
  biddingStrategy: BiddingStrategySchema,
  defaultBid: z.string(),
  placements: z.object(
    Object.fromEntries(PLACEMENTS.map((placement) => [placement, placementPercent])) as Record<
      Placement,
      typeof placementPercent
    >,
  ),
});
type SettingsValues = z.infer<typeof settingsSchema>;

interface CampaignSheetProps {
  campaign: CompiledTemplateCampaign | null;
  override: TemplateCampaignOverride | undefined;
  onClose: () => void;
  /** Persists the merged override; resolves false when the save failed. */
  onSave: (override: TemplateCampaignOverride) => Promise<boolean>;
}

/** 活动详情（编辑）drawer — Paper TP-09a "Campaign Sheet". */
export function CampaignSheet({ campaign, override, onClose, onSave }: CampaignSheetProps) {
  return (
    <Sheet open={campaign !== null} onOpenChange={(open) => !open && onClose()}>
      <SheetContent
        side="right"
        showCloseButton={false}
        className="w-[640px] gap-0 bg-surface p-0 data-[side=right]:w-[640px] data-[side=right]:sm:max-w-[640px]"
      >
        {campaign ? (
          <CampaignSheetBody
            key={campaign.key}
            campaign={campaign}
            override={override}
            onClose={onClose}
            onSave={onSave}
          />
        ) : null}
      </SheetContent>
    </Sheet>
  );
}

function CampaignSheetBody({
  campaign,
  override,
  onClose,
  onSave,
}: CampaignSheetProps & { campaign: CompiledTemplateCampaign }) {
  const { t } = useTranslation("templatePortfolio");
  const { campaign: settings, adGroup, negatives } = campaign.payload;
  const automatic = isAutomatic(campaign);
  const product = adGroup.products[0];

  const form = useForm<SettingsValues>({
    resolver: zodResolver(settingsSchema),
    defaultValues: {
      name: campaign.name,
      dailyBudget: settings.dailyBudget.toLocaleString("en-US"),
      biddingStrategy: settings.biddingStrategy,
      defaultBid: automatic ? "" : campaignDefaultBid(campaign).toFixed(2),
      placements: Object.fromEntries(
        PLACEMENTS.map((placement) => [
          placement,
          String(
            settings.placementAdjustments.find((p) => p.placement === placement)?.percentage ?? "",
          ),
        ]),
      ) as Record<Placement, string>,
    },
  });
  const errors = form.formState.errors;
  // Controlled binding: the Base UI input does not take RHF's uncontrolled defaults.
  const bind = (name: FieldPath<SettingsValues>) => ({
    name,
    value: form.watch(name) as string,
    onChange: (event: React.ChangeEvent<HTMLInputElement>) =>
      form.setValue(name, event.target.value, { shouldDirty: true }),
  });

  const [editingName, setEditingName] = useState(false);
  const nameBeforeEdit = useRef(campaign.name);

  const targets = useMemo(() => campaignTargets(campaign), [campaign]);
  const [bidTexts, setBidTexts] = useState<Record<string, string>>({});
  const [removedTargets, setRemovedTargets] = useState<string[]>([]);
  const originalNegatives = useMemo(() => negatives.keywords.map((n) => n.text), [negatives]);
  const [negativeTexts, setNegativeTexts] = useState(originalNegatives);
  const [invalidBids, setInvalidBids] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState(false);

  const visibleTargets = targets.filter((target) => !removedTargets.includes(target.key));
  // 保存 stays disabled until something differs from what the drawer opened with.
  const bidsChanged = Object.entries(bidTexts).some(([key, text]) => {
    const bid = parseAmount(text);
    return bid === null || bid !== targets.find((target) => target.key === key)?.bid;
  });
  const negativesChanged =
    negativeTexts.length !== originalNegatives.length ||
    negativeTexts.some((text, index) => text !== originalNegatives[index]);
  const hasChanges =
    form.formState.isDirty || bidsChanged || removedTargets.length > 0 || negativesChanged;
  const keywordCampaign = targets[0]?.kind === "keyword" || targets.length === 0;

  const submit = form.handleSubmit(async (values) => {
    const targetBids: Record<string, number> = {};
    const invalid = new Set<string>();
    for (const [key, text] of Object.entries(bidTexts)) {
      if (removedTargets.includes(key)) continue;
      const bid = parseAmount(text);
      if (bid === null || bid <= 0) invalid.add(key);
      else targetBids[key] = bid;
    }
    setInvalidBids(invalid);
    const defaultBid = automatic ? undefined : parseAmount(values.defaultBid);
    if (invalid.size > 0 || defaultBid === null || (defaultBid !== undefined && defaultBid <= 0)) {
      if (defaultBid === null || (defaultBid !== undefined && defaultBid <= 0)) {
        form.setError("defaultBid", { type: "min" });
      }
      return;
    }
    const lower = new Set(negativeTexts.map((text) => text.toLowerCase()));
    const originalLower = new Set(originalNegatives.map((text) => text.toLowerCase()));
    const next = mergeOverride(campaign, override, {
      name: values.name,
      dailyBudget: parseAmount(values.dailyBudget) as number,
      biddingStrategy: values.biddingStrategy,
      defaultBid,
      placements: Object.fromEntries(
        PLACEMENTS.flatMap((placement) => {
          const value = parseAmount(values.placements[placement]);
          return value === null ? [] : [[placement, value]];
        }),
      ),
      targetBids,
      removedTargets,
      addedNegatives: negativeTexts.filter((text) => !originalLower.has(text.toLowerCase())),
      removedNegatives: originalNegatives.filter((text) => !lower.has(text.toLowerCase())),
    });
    if (!next) {
      onClose();
      return;
    }
    setSaving(true);
    const ok = await onSave(next);
    setSaving(false);
    if (ok) onClose();
  });

  const strategyItems = BIDDING_STRATEGIES.map((value) => ({
    value,
    label: t(`preview.strategies.${value}`),
  }));
  const [tab, setTab] = useState<"targets" | "negatives">("targets");

  return (
    <form
      className="flex min-h-0 flex-1 flex-col"
      onSubmit={(event) => {
        event.preventDefault();
        void submit();
      }}
    >
      <header className="flex items-start justify-between gap-4 border-b border-line px-6 pt-5 pb-4">
        <div className="flex min-w-0 flex-1 flex-col gap-1.5">
          <div className="flex min-w-0 items-center gap-2">
            {editingName ? (
              <Input
                autoFocus
                {...bind("name")}
                onBlur={() => setEditingName(false)}
                aria-label={t("preview.drawer.editName")}
                aria-invalid={Boolean(errors.name) || undefined}
                className="h-8 text-base font-semibold"
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    event.preventDefault();
                    setEditingName(false);
                  } else if (event.key === "Escape") {
                    event.preventDefault();
                    event.stopPropagation();
                    form.setValue("name", nameBeforeEdit.current, { shouldDirty: true });
                    setEditingName(false);
                  }
                }}
              />
            ) : (
              <>
                <SheetTitle
                  className={cn(
                    "truncate font-sans text-base font-semibold leading-6 text-ink",
                    errors.name && "text-destructive",
                  )}
                >
                  {form.watch("name")}
                </SheetTitle>
                <button
                  type="button"
                  aria-label={t("preview.drawer.editName")}
                  className="grid size-6 shrink-0 place-items-center rounded-md hover:bg-accent"
                  onClick={() => {
                    nameBeforeEdit.current = form.getValues("name");
                    setEditingName(true);
                  }}
                >
                  <IconPencil className="size-3.5 text-muted-foreground" stroke={2} />
                </button>
              </>
            )}
          </div>
          <SheetDescription className="text-[13px] leading-5 text-text-secondary">
            {t("preview.drawer.subtitle", {
              targeting: automatic ? t("preview.drawer.auto") : t("preview.drawer.manual"),
              group: t(`preview.groups.${groupOfLayer(campaign.layer)}`),
            })}
          </SheetDescription>
        </div>
        <button
          type="button"
          aria-label={t("preview.drawer.close")}
          onClick={onClose}
          className="grid size-7 shrink-0 place-items-center rounded-md hover:bg-accent"
        >
          <IconX className="size-4 text-muted-foreground" stroke={2} />
        </button>
      </header>

      <div className="paper-scrollbar flex min-h-0 flex-1 flex-col overflow-y-auto">
        <section className="flex flex-col gap-3 px-6 py-5">
          <h3 className="text-[13px] font-semibold leading-4 text-ink">
            {t("preview.drawer.settings")}
          </h3>
          <div className="flex flex-wrap gap-y-3.5 rounded-lg border border-line p-4">
            <SettingCell label={t("preview.drawer.dailyBudget")}>
              <AffixInput
                prefix="$"
                className="w-[220px]"
                aria-label={t("preview.drawer.dailyBudget")}
                aria-invalid={Boolean(errors.dailyBudget) || undefined}
                {...bind("dailyBudget")}
              />
            </SettingCell>
            <SettingCell label={t("preview.drawer.biddingStrategy")}>
              <Controller
                control={form.control}
                name="biddingStrategy"
                render={({ field }) => (
                  <Select
                    items={strategyItems}
                    value={field.value}
                    onValueChange={(value) => value && field.onChange(value)}
                  >
                    <SelectTrigger
                      className="h-9 w-[240px]"
                      aria-label={t("preview.drawer.biddingStrategy")}
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {strategyItems.map((item) => (
                        <SelectItem key={item.value} value={item.value}>
                          {item.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
            </SettingCell>
            {automatic ? null : (
              <SettingCell label={t("preview.drawer.defaultBid")}>
                <AffixInput
                  prefix="$"
                  className="w-[220px]"
                  aria-label={t("preview.drawer.defaultBid")}
                  aria-invalid={Boolean(errors.defaultBid) || undefined}
                  {...bind("defaultBid")}
                />
              </SettingCell>
            )}
            <SettingCell label={t("preview.drawer.placements")}>
              <div className="flex gap-1.5">
                {PLACEMENTS.map((placement) => (
                  <AffixInput
                    key={placement}
                    suffix="%"
                    inputMode="numeric"
                    className="w-14 [&_[data-slot=input-group-addon]]:pe-2! [&_input]:ps-2! [&_input]:pe-0.5!"
                    aria-label={t(`preview.placements.${placement}`)}
                    aria-invalid={Boolean(errors.placements?.[placement]) || undefined}
                    {...bind(`placements.${placement}`)}
                  />
                ))}
              </div>
            </SettingCell>
            <SettingCell label={t("preview.drawer.adGroup")}>
              <span className="text-sm text-ink">{adGroup.name}</span>
            </SettingCell>
            <SettingCell label={t("preview.drawer.product")}>
              <span className="text-sm text-ink">
                {product ? [product.sku, product.asin].filter(Boolean).join(" · ") : "—"}
              </span>
            </SettingCell>
          </div>
        </section>

        <Tabs
          value={tab}
          onValueChange={(value) => setTab(value as typeof tab)}
          className="flex flex-col gap-3 px-6 pt-1 pb-5"
        >
          <TabsList className="w-full justify-start gap-5 border-line [&_[data-slot=tab-indicator]]:bg-ink">
            <SheetTab
              value="targets"
              active={tab === "targets"}
              label={
                keywordCampaign ? t("preview.drawer.keywordTab") : t("preview.drawer.targetTab")
              }
              count={visibleTargets.length}
            />
            <SheetTab
              value="negatives"
              active={tab === "negatives"}
              label={t("preview.drawer.negativeTab")}
              count={negativeTexts.length}
            />
          </TabsList>
          <TabsPanel value="targets">
            <TargetsTable
              targets={visibleTargets}
              bidTexts={bidTexts}
              invalidBids={invalidBids}
              onBidChange={(key, text) => setBidTexts((previous) => ({ ...previous, [key]: text }))}
              onRemove={(key) => setRemovedTargets((previous) => [...previous, key])}
            />
          </TabsPanel>
          <TabsPanel value="negatives">
            <NegativesTable
              texts={negativeTexts}
              onAdd={(text) => setNegativeTexts((previous) => [text, ...previous])}
              onRemove={(text) =>
                setNegativeTexts((previous) => previous.filter((item) => item !== text))
              }
            />
          </TabsPanel>
        </Tabs>
      </div>

      <footer className="flex shrink-0 justify-end gap-2 border-t border-line bg-surface px-6 py-3">
        <Button type="button" variant="outline" onClick={onClose} disabled={saving}>
          {t("preview.drawer.cancel")}
        </Button>
        <Button type="submit" loading={saving} disabled={!hasChanges}>
          {t("preview.drawer.save")}
        </Button>
      </footer>
    </form>
  );
}

function SettingCell({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex w-1/2 flex-col gap-1">
      <span className="text-xs text-text-secondary">{label}</span>
      {children}
    </div>
  );
}

function SheetTab({
  value,
  active,
  label,
  count,
}: {
  value: string;
  active: boolean;
  label: string;
  count: number;
}) {
  return (
    <TabsTab
      value={value}
      className="h-auto gap-1.5 px-0 pb-2.5 text-[13px] font-normal leading-4 text-text-secondary data-active:font-semibold data-active:text-ink"
    >
      {label}
      <span
        className={cn(
          "rounded-full px-1.5 text-xs leading-4 font-normal tabular-nums",
          active ? "bg-ink text-white" : "bg-canvas text-text-secondary",
        )}
      >
        {count.toLocaleString()}
      </span>
    </TabsTab>
  );
}

function usePage<T>(items: T[]) {
  const [page, setPage] = useState(1);
  const pageCount = Math.max(1, Math.ceil(items.length / PAGE_SIZE));
  const current = Math.min(page, pageCount);
  return {
    rows: items.slice((current - 1) * PAGE_SIZE, current * PAGE_SIZE),
    pager: (
      <Pager
        total={items.length}
        page={current}
        pageCount={pageCount}
        onChange={(next) => setPage(next)}
      />
    ),
  };
}

function Pager({
  total,
  page,
  pageCount,
  onChange,
}: {
  total: number;
  page: number;
  pageCount: number;
  onChange: (page: number) => void;
}) {
  const { t } = useTranslation("templatePortfolio");
  const button =
    "grid size-7 place-items-center rounded-md border border-line text-ink disabled:text-muted-foreground disabled:cursor-default";
  return (
    <div className="flex items-center justify-between">
      <span className="text-xs text-text-secondary">
        {t("preview.drawer.total", { count: total })}
      </span>
      <div className="flex items-center gap-2">
        <button
          type="button"
          className={button}
          disabled={page <= 1}
          aria-label={t("preview.drawer.previousPage")}
          onClick={() => onChange(page - 1)}
        >
          <IconChevronLeft className="size-3.5" stroke={2} />
        </button>
        <span className="font-mono text-xs text-ink">
          {page} / {pageCount}
        </span>
        <button
          type="button"
          className={button}
          disabled={page >= pageCount}
          aria-label={t("preview.drawer.nextPage")}
          onClick={() => onChange(page + 1)}
        >
          <IconChevronRight className="size-3.5" stroke={2} />
        </button>
      </div>
    </div>
  );
}

const TABLE = "flex flex-col overflow-clip rounded-lg border border-line";
const HEAD = "flex h-9 shrink-0 items-center bg-canvas px-3 text-xs text-text-secondary";
const ROW = "flex h-10 shrink-0 items-center border-t border-line px-3 text-[13px] leading-4";

function RemoveButton({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      aria-label={label}
      onClick={onClick}
      className="flex h-7 w-7 shrink-0 items-center justify-end text-muted-foreground hover:text-destructive"
    >
      <IconTrash className="size-3.5" stroke={2} />
    </button>
  );
}

function TargetsTable({
  targets,
  bidTexts,
  invalidBids,
  onBidChange,
  onRemove,
}: {
  targets: PreviewTarget[];
  bidTexts: Record<string, string>;
  invalidBids: Set<string>;
  onBidChange: (key: string, text: string) => void;
  onRemove: (key: string) => void;
}) {
  const { t } = useTranslation("templatePortfolio");
  const { rows, pager } = usePage(targets);
  const kind = targets[0]?.kind ?? "keyword";

  if (targets.length === 0) {
    return (
      <p className="py-8 text-center text-xs text-muted-foreground">
        {t("preview.drawer.emptyTargets")}
      </p>
    );
  }

  const describe = (target: PreviewTarget): { label: string; detail: string } => {
    switch (target.kind) {
      case "keyword":
        return { label: target.label, detail: t(`preview.matchTypes.${target.detail}`) };
      case "auto":
        return {
          label: t(`preview.autoGroups.${target.detail}`),
          detail: target.enabled ? t("preview.drawer.enabled") : t("preview.drawer.paused"),
        };
      case "category":
        return {
          label: target.label,
          detail:
            target.priceGreaterThan !== undefined && target.priceLessThan !== undefined
              ? `$${target.priceGreaterThan}–$${target.priceLessThan}`
              : "—",
        };
      default:
        return { label: t("preview.drawer.themeLabel"), detail: "—" };
    }
  };

  return (
    <div className="flex flex-col gap-3">
      <div className={TABLE}>
        <div className={HEAD}>
          <span className="min-w-0 flex-1">
            {kind === "keyword" ? t("preview.drawer.keyword") : t("preview.drawer.target")}
          </span>
          <span className="w-24 shrink-0">
            {kind === "keyword"
              ? t("preview.drawer.matchType")
              : kind === "auto"
                ? t("preview.drawer.status")
                : kind === "category"
                  ? t("preview.drawer.priceBand")
                  : ""}
          </span>
          <span className="w-20 shrink-0 text-right">{t("preview.drawer.bid")}</span>
          <span className="w-7 shrink-0" />
        </div>
        {rows.map((target) => {
          const { label, detail } = describe(target);
          return (
            <div key={target.key} className={ROW}>
              <span className="min-w-0 flex-1 truncate text-ink">{label}</span>
              <span className="w-24 shrink-0 text-text-secondary">{detail}</span>
              <AffixInput
                prefix="$"
                className="h-7 w-20 rounded-md [&_input]:px-1.5"
                aria-label={`${label} ${t("preview.drawer.bid")}`}
                aria-invalid={invalidBids.has(target.key) || undefined}
                value={bidTexts[target.key] ?? (target.bid === null ? "" : target.bid.toFixed(2))}
                onChange={(event) => onBidChange(target.key, event.target.value)}
              />
              {target.removable && targets.length > 1 ? (
                <RemoveButton
                  label={t("preview.drawer.removeTarget")}
                  onClick={() => onRemove(target.key)}
                />
              ) : (
                <span className="w-7 shrink-0" />
              )}
            </div>
          );
        })}
      </div>
      {pager}
    </div>
  );
}

function NegativesTable({
  texts,
  onAdd,
  onRemove,
}: {
  texts: string[];
  onAdd: (text: string) => void;
  onRemove: (text: string) => void;
}) {
  const { t } = useTranslation("templatePortfolio");
  const [draft, setDraft] = useState("");
  const { rows, pager } = usePage(texts);

  function add() {
    const text = draft.trim();
    setDraft("");
    if (
      text === "" ||
      text.length > MAX_NEGATIVE_LENGTH ||
      texts.some((item) => item.toLowerCase() === text.toLowerCase())
    ) {
      return;
    }
    onAdd(text);
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex gap-2">
        <Input
          value={draft}
          placeholder={t("preview.drawer.negativePlaceholder")}
          aria-label={t("preview.drawer.negativePlaceholder")}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              add();
            }
          }}
        />
        <Button type="button" variant="outline" onClick={add}>
          {t("preview.drawer.addNegative")}
        </Button>
      </div>
      {texts.length === 0 ? (
        <p className="py-8 text-center text-xs text-muted-foreground">
          {t("preview.drawer.emptyNegatives")}
        </p>
      ) : (
        <>
          <div className={TABLE}>
            <div className={HEAD}>
              <span className="min-w-0 flex-1">{t("preview.drawer.keyword")}</span>
              <span className="w-24 shrink-0">{t("preview.drawer.matchType")}</span>
              <span className="w-7 shrink-0" />
            </div>
            {rows.map((text) => (
              <div key={text} className={ROW}>
                <span className="min-w-0 flex-1 truncate text-ink">{text}</span>
                <span className="w-24 shrink-0 text-text-secondary">
                  {t("preview.drawer.negativeExact")}
                </span>
                <RemoveButton
                  label={t("preview.drawer.removeTarget")}
                  onClick={() => onRemove(text)}
                />
              </div>
            ))}
          </div>
          {pager}
        </>
      )}
    </div>
  );
}
